import type { ShipResponsePiece } from '../../../apis/fedexQuote';

export interface BookedLabel {
  sequence: number;
  trackingNumber: string;
  labelBase64: string;
  labelContentType: string;
}

interface ShipLabelSource {
  trackingNumber?: string | null;
  labelBase64?: string | null;
  labelContentType?: string | null;
  pieces?: ShipResponsePiece[];
}

export const isImageContentType = (mime?: string | null): boolean =>
  !!mime && /^image\/[\w.+-]+$/i.test(mime);

export const isZplContentType = (mime?: string | null): boolean =>
  !!mime && /zpl|epl/i.test(mime);

/** Labels of a ship response in box order; the flat label stands in for box 1 when no pieces came back. */
export const bookedLabels = (data: ShipLabelSource): BookedLabel[] => {
  const fromPieces = (data.pieces ?? [])
    .filter((p) => p.trackingNumber && p.labelBase64)
    .map((p) => ({
      sequence: p.sequence,
      trackingNumber: p.trackingNumber as string,
      labelBase64: p.labelBase64 as string,
      labelContentType: p.labelContentType || 'application/pdf',
    }))
    .sort((a, b) => a.sequence - b.sequence);
  if (fromPieces.length) return fromPieces;
  return data.trackingNumber && data.labelBase64
    ? [
        {
          sequence: 1,
          trackingNumber: data.trackingNumber,
          labelBase64: data.labelBase64,
          labelContentType: data.labelContentType || 'application/pdf',
        },
      ]
    : [];
};

/** One ZPL stream for the printer, labels in box order. */
export const concatZpl = (labels: BookedLabel[]): string =>
  labels.map((l) => atob(l.labelBase64)).join('\n');

/** A print page with one image per label, printed once every image has loaded. */
export const buildPrintHtml = (srcs: string[]): string =>
  `<!DOCTYPE html><html><head><title>Print Labels</title><style>
    body{margin:0;}
    img{width:100%;height:auto;display:block;page-break-after:always;}
    img:last-child{page-break-after:auto;}
  </style></head><body>${srcs.map((src) => `<img src="${src}">`).join('')}
  <script>Promise.all([...document.images].map((i)=>i.complete?1:new Promise((r)=>{i.onload=i.onerror=r}))).then(()=>window.print())</script>
  </body></html>`;

/** The banner for a multi-box booking whose label count differs from the box count, else null. */
export const labelCountWarning = (
  requested: number,
  returned: number,
  provider: string
): string | null => {
  if (requested === returned) return null;
  const count = `${returned} label${returned === 1 ? '' : 's'}`;
  return `${requested} boxes were requested but ${provider} returned ${count}. ${
    returned === 0
      ? `The booking went through; get the labels from the ${provider} portal.`
      : `The labels below are what was booked; void any extra in the ${provider} portal.`
  }`;
};
